/**
 * LoadingState — skeleton screen matching the actual content layout.
 *
 * Shows shimmer placeholders for:
 * - RAF Summary card
 * - 3 suggestion cards (one suspect, two recapture)
 *
 * This gives clinicians immediate layout orientation instead of a blank spinner.
 */
import { useSession } from '../context/SessionContext';

function Shimmer({ className }: { className: string }) {
  return (
    <div
      className={`bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse rounded ${className}`}
      style={{
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s ease-in-out infinite',
      }}
    />
  );
}

function SkeletonRAFCard() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div className="space-y-1.5">
          <Shimmer className="h-3.5 w-32" />
          <Shimmer className="h-2.5 w-24" />
        </div>
        <Shimmer className="h-7 w-20" />
      </div>
      {/* Gauge 1 */}
      <div className="space-y-1.5 mb-3">
        <div className="flex justify-between">
          <Shimmer className="h-2.5 w-28" />
          <Shimmer className="h-2.5 w-10" />
        </div>
        <Shimmer className="h-2 w-full rounded-full" />
      </div>
      {/* Gauge 2 */}
      <div className="space-y-1.5 mb-4">
        <div className="flex justify-between">
          <Shimmer className="h-2.5 w-40" />
          <Shimmer className="h-2.5 w-10" />
        </div>
        <Shimmer className="h-2 w-full rounded-full" />
      </div>
      {/* Chips */}
      <div className="flex gap-2">
        <Shimmer className="h-6 w-20 rounded-full" />
        <Shimmer className="h-6 w-20 rounded-full" />
        <Shimmer className="h-6 w-16 rounded-full" />
      </div>
    </div>
  );
}

function SkeletonSuggestionCard({ leftColor }: { leftColor: string }) {
  return (
    <div
      className="bg-white border border-gray-200 rounded-lg border-l-4 p-4 space-y-3"
      style={{ borderLeftColor: leftColor }}
    >
      {/* Row 1: badge + code + action */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Shimmer className="h-5 w-16 rounded-full flex-shrink-0" />
          <Shimmer className="h-5 w-12 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Shimmer className="h-3.5 w-full" />
            <Shimmer className="h-2.5 w-2/3" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Shimmer className="h-7 w-16 rounded" />
          <Shimmer className="h-7 w-14 rounded" />
        </div>
      </div>
      {/* Row 2: confidence + dates */}
      <div className="flex items-center gap-3">
        <Shimmer className="h-1.5 w-16 rounded-full" />
        <Shimmer className="h-2.5 w-20" />
        <Shimmer className="h-2.5 w-24" />
      </div>
    </div>
  );
}

export function LoadingState() {
  const { launch } = useSession();
  const brandBg = launch.theme === 'ecw' ? 'bg-ecw-teal-light' : 'bg-cerner-blue-light';
  const accentColor = launch.theme === 'ecw' ? '#00897B' : '#0079C1';

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div className="flex flex-col h-full overflow-hidden">
        {/* Patient banner skeleton */}
        <div className={`px-4 py-2.5 ${brandBg} border-b border-gray-200`}>
          <div className="flex items-center gap-3 mb-2">
            <Shimmer className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="space-y-1 flex-1">
              <Shimmer className="h-4 w-44" />
              <Shimmer className="h-2.5 w-72" />
            </div>
            <Shimmer className="h-3 w-32" />
          </div>
        </div>

        {/* RAF card skeleton */}
        <div className={`px-4 py-3 ${brandBg} border-b border-gray-200`}>
          {/* App header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-6 rounded-full" style={{ background: accentColor }} />
            <div className="space-y-1">
              <Shimmer className="h-3.5 w-16" />
              <Shimmer className="h-2.5 w-32" />
            </div>
          </div>
          <SkeletonRAFCard />
        </div>

        {/* Filter bar skeleton */}
        <div className="px-4 py-2.5 border-b border-gray-200 bg-white flex items-center gap-2">
          <Shimmer className="h-7 flex-1 rounded-md" />
          <Shimmer className="h-7 w-20 rounded" />
          <Shimmer className="h-7 w-20 rounded" />
          <Shimmer className="h-7 w-24 rounded" />
        </div>

        {/* Suggestion card skeletons */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Section header */}
          <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
            <div className="w-0.5 h-5 rounded-full" style={{ background: accentColor }} />
            <Shimmer className="h-3.5 w-28" />
            <Shimmer className="h-4 w-6 rounded-full" />
          </div>

          <SkeletonSuggestionCard leftColor="#1E40AF" />
          <SkeletonSuggestionCard leftColor="#1E40AF" />

          <div className="flex items-center gap-2 pb-1 border-b border-gray-200 pt-1">
            <div className="w-0.5 h-5 rounded-full bg-recapture" />
            <Shimmer className="h-3.5 w-32" />
            <Shimmer className="h-4 w-6 rounded-full" />
          </div>

          <SkeletonSuggestionCard leftColor="#6D28D9" />
          <SkeletonSuggestionCard leftColor="#6D28D9" />
          <SkeletonSuggestionCard leftColor="#B45309" />
        </div>
      </div>
    </>
  );
}
